import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import PlatoForm from './PlatoForm'
import QuickMenuSelector from './QuickMenuSelector'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { supabase } from '../../lib/supabase'
import Toast from '../ui/Toast'

const { FiPlus, FiEdit, FiTrash2, FiEye, FiEyeOff, FiFilter, FiAlertTriangle, FiZap } = FiIcons

const PlatosManager = () => {
  const [platos, setPlatos] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [showQuickSelector, setShowQuickSelector] = useState(false)
  const [editingPlato, setEditingPlato] = useState(null)
  const [toast, setToast] = useState(null)
  const [filters, setFilters] = useState({
    fecha: format(new Date(), 'yyyy-MM-dd'),
    categoria: ''
  })
  const [menuValidation, setMenuValidation] = useState({
    primeros: 0,
    segundos: 0,
    isWeekday: true
  })

  useEffect(() => {
    fetchPlatos()
  }, [filters])

  useEffect(() => {
    validateMenu()
  }, [platos, filters.fecha])

  const fetchPlatos = async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('platos')
        .select('*')
        .order('categoria')
        .order('orden')

      if (filters.fecha) {
        query = query.eq('fecha', filters.fecha)
      }

      if (filters.categoria) {
        query = query.eq('categoria', filters.categoria)
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching platos:', error)
        showToast('Error al cargar los platos', 'error')
        setPlatos([])
      } else {
        console.log('Fetched platos from admin:', data) // Debug log
        setPlatos(data || [])
      }
    } catch (error) {
      console.error('Error fetching platos:', error)
      showToast('Error al cargar los platos', 'error')
      setPlatos([])
    } finally {
      setLoading(false)
    }
  }

  const validateMenu = () => {
    if (!filters.fecha) return

    const date = new Date(filters.fecha)
    const dayOfWeek = date.getDay()
    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5

    const primeros = platos.filter(p => p.categoria === 'primero' && p.visible).length
    const segundos = platos.filter(p => p.categoria === 'segundo' && p.visible).length

    setMenuValidation({ primeros, segundos, isWeekday })
  }

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  const handleSavePlato = async (platoData) => {
    try {
      if (editingPlato) {
        // Update existing plato
        const { error } = await supabase
          .from('platos')
          .update(platoData)
          .eq('id', editingPlato.id)

        if (error) {
          console.error('Error updating plato:', error)
          showToast('Error al actualizar el plato', 'error')
          return
        }

        showToast('Plato actualizado correctamente')
      } else {
        // Add new plato
        const { error } = await supabase
          .from('platos')
          .insert([platoData])

        if (error) {
          console.error('Error creating plato:', error)
          showToast('Error al crear el plato', 'error')
          return
        }

        showToast('Plato creado correctamente')
      }

      setShowForm(false)
      setEditingPlato(null)
      fetchPlatos()
    } catch (error) {
      console.error('Error saving plato:', error)
      showToast('Error al guardar el plato', 'error')
    }
  }

  const handleEditPlato = (plato) => {
    setEditingPlato(plato)
    setShowForm(true)
  }

  const handleDeletePlato = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este plato?')) {
      try {
        const { error } = await supabase
          .from('platos')
          .delete()
          .eq('id', id)

        if (error) {
          console.error('Error deleting plato:', error)
          showToast('Error al eliminar el plato', 'error')
          return
        }

        showToast('Plato eliminado correctamente')
        fetchPlatos()
      } catch (error) {
        console.error('Error deleting plato:', error)
        showToast('Error al eliminar el plato', 'error')
      }
    }
  }

  const toggleVisibility = async (id) => {
    try {
      const plato = platos.find(p => p.id === id)
      if (!plato) {
        showToast('Plato no encontrado', 'error')
        return
      }

      console.log('Toggling visibility for plato:', plato) // Debug log
      console.log('Current visible value:', plato.visible, 'New value:', !plato.visible) // Debug log

      const { data, error } = await supabase
        .from('platos')
        .update({ visible: !plato.visible })
        .eq('id', id)
        .select()

      if (error) {
        console.error('Error toggling visibility:', error)
        showToast('Error al cambiar la visibilidad', 'error')
        return
      }

      console.log('Update result:', data) // Debug log
      showToast(`Plato ${!plato.visible ? 'mostrado' : 'ocultado'} correctamente`)
      
      // Refresh the data
      await fetchPlatos()
    } catch (error) {
      console.error('Error toggling visibility:', error)
      showToast('Error al cambiar la visibilidad', 'error')
    }
  }

  const categoryNames = {
    primero: 'Primero',
    segundo: 'Segundo',
    tipico: 'Típico Boliviano',
    parrilla: 'Parrilla',
    bebida_casa: 'Bebida Casa',
    bebida_botella: 'Bebida Botella',
    cerveza: 'Cerveza'
  }

  const getCategoryColor = (categoria) => {
    const colors = {
      primero: 'bg-blue-100 text-blue-800',
      segundo: 'bg-green-100 text-green-800',
      tipico: 'bg-yellow-100 text-yellow-800',
      parrilla: 'bg-red-100 text-red-800',
      bebida_casa: 'bg-purple-100 text-purple-800',
      bebida_botella: 'bg-indigo-100 text-indigo-800',
      cerveza: 'bg-orange-100 text-orange-800'
    }
    return colors[categoria] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-vip-orange">Gestión de Platos</h1>
          <p className="text-vip-brown">Administra el menú del restaurante</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowQuickSelector(true)}
            className="flex items-center px-4 py-2 bg-gradient-to-r from-vip-orange to-vip-dark text-white rounded-md hover:from-vip-dark hover:to-vip-orange transition-all duration-200"
          >
            <SafeIcon icon={FiZap} className="mr-2" />
            Selector Rápido
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 bg-vip-orange text-white rounded-md hover:bg-vip-dark transition-colors duration-200"
          >
            <SafeIcon icon={FiPlus} className="mr-2" />
            Nuevo Plato
          </button>
        </div>
      </div>

      {/* Menu Validation Warning */}
      {menuValidation.isWeekday && filters.fecha && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg border ${
            menuValidation.primeros < 3 || menuValidation.segundos < 3
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-green-50 border-green-200'
          }`}
        >
          <div className="flex items-center">
            <SafeIcon 
              icon={FiAlertTriangle} 
              className={`mr-2 ${
                menuValidation.primeros < 3 || menuValidation.segundos < 3
                  ? 'text-yellow-600'
                  : 'text-green-600'
              }`} 
            />
            <div>
              <h4 className={`font-semibold ${
                menuValidation.primeros < 3 || menuValidation.segundos < 3
                  ? 'text-yellow-900'
                  : 'text-green-900'
              }`}>
                Validación del Menú del Día
              </h4>
              <p className={`text-sm ${
                menuValidation.primeros < 3 || menuValidation.segundos < 3
                  ? 'text-yellow-800'
                  : 'text-green-800'
              }`}>
                Primeros: {menuValidation.primeros}/3 • Segundos: {menuValidation.segundos}/3
                {menuValidation.primeros < 3 || menuValidation.segundos < 3
                  ? ' - ¡Faltan platos para completar el menú!'
                  : ' - Menú completo ✓'
                }
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="flex items-center gap-4 mb-4">
          <SafeIcon icon={FiFilter} className="text-vip-brown" />
          <h3 className="font-semibold text-vip-brown">Filtros</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-vip-brown mb-2">
              Fecha
            </label>
            <input
              type="date"
              value={filters.fecha}
              onChange={(e) => setFilters(prev => ({ ...prev, fecha: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-vip-brown mb-2">
              Categoría
            </label>
            <select
              value={filters.categoria}
              onChange={(e) => setFilters(prev => ({ ...prev, categoria: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange"
            >
              <option value="">Todas las categorías</option>
              <option value="primero">Primeros</option>
              <option value="segundo">Segundos</option>
              <option value="tipico">Típicos Bolivianos</option>
              <option value="parrilla">Parrilla</option>
              <option value="bebida_casa">Bebidas Casa</option>
              <option value="bebida_botella">Bebidas Botella</option>
              <option value="cerveza">Cervezas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Platos List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-vip-orange"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {platos.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No hay platos para los filtros seleccionados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoría
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Precio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {platos.map((plato) => (
                    <motion.tr
                      key={plato.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {plato.nombre}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {plato.descripcion}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(plato.categoria)}`}>
                          {categoryNames[plato.categoria]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {plato.precio ? `${plato.precio.toFixed(2)}€` : 'Menú del día'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          plato.visible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {plato.visible ? 'Visible' : 'Oculto'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => toggleVisibility(plato.id)}
                            className="text-gray-400 hover:text-vip-brown transition-colors duration-200"
                            title={plato.visible ? 'Ocultar' : 'Mostrar'}
                          >
                            <SafeIcon icon={plato.visible ? FiEye : FiEyeOff} />
                          </button>
                          <button
                            onClick={() => handleEditPlato(plato)}
                            className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                            title="Editar"
                          >
                            <SafeIcon icon={FiEdit} />
                          </button>
                          <button
                            onClick={() => handleDeletePlato(plato.id)}
                            className="text-red-600 hover:text-red-900 transition-colors duration-200"
                            title="Eliminar"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <PlatoForm
          plato={editingPlato}
          onSave={handleSavePlato}
          onCancel={() => {
            setShowForm(false)
            setEditingPlato(null)
          }}
        />
      )}

      {/* Quick Selector Modal */}
      {showQuickSelector && (
        <QuickMenuSelector
          targetDate={filters.fecha}
          onClose={() => setShowQuickSelector(false)}
          onSuccess={() => {
            fetchPlatos()
            showToast('Platos añadidos correctamente al menú')
          }}
        />
      )}
    </div>
  )
}

export default PlatosManager